import { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "tlr_pwa_prompt_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * Floating "Add to Home Screen" banner. Uses the native beforeinstallprompt
 * flow on Android/desktop Chrome; shows a manual Share-sheet hint on iOS
 * Safari (which has no install API). Dismissed once, remembered forever.
 */
export default function InstallPWAPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("android");

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    if (isIOS()) {
      setPlatform("ios");
      const t = setTimeout(() => setVisible(true), 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setPlatform("android");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    await deferredEvent.userChoice;
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 bg-white border border-brand-subtle rounded-2xl shadow-2xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="w-11 h-11 rounded-xl bg-[#7A1E2A] flex items-center justify-center text-white shrink-0">
        <Download size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-base brand-forest leading-tight">Add To-Let Rentals to your home screen</p>
        {platform === "ios" ? (
          <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1 flex-wrap">
            Tap <Share size={12} className="brand-forest" /> Share, then "Add to Home Screen"
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-500 mt-1">Quick access, like a real app — no app store needed.</p>
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="mt-3 text-xs font-bold uppercase tracking-widest bg-brand-forest text-white rounded-full px-4 py-2 hover:bg-[#5C1420] transition-colors"
            >
              Install app
            </button>
          </>
        )}
      </div>
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        className="text-gray-400 hover:text-gray-600 shrink-0"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
